import styled from 'styled-components'

import { IconCaret } from 'assets/svg/sprite'
import LiveActions from 'features/LiveStream/LiveActions/LiveActions'
import LiveStats from 'features/LiveStream/LiveStats/LiveStats'
import { AllIcons } from 'helpers/allIcons'
import { useStreamingStore } from 'store/streaming'
import { useViewerStore } from 'store/viewer'

import CloseButton from './components/CloseButton'

type BaseProps = {
  creatorUsername?: string
  creatorId?: string
  avatarUrl?: string
  commentCount?: number
  onToggleComments?: () => void
}

type ViewerProps = BaseProps & {
  role: 'viewer'
  onOpenTipModal: () => void
  onRequestClose: () => void
}

type CreatorProps = BaseProps & {
  role: 'creator'
  onOpenTipModal?: never
  onRequestClose: () => void
}

export function Header(props: ViewerProps | CreatorProps) {
  const { role, creatorUsername, avatarUrl, commentCount } = props

  const isStreaming = useStreamingStore(s => s.isStreaming)
  const creatorStreamId = useStreamingStore(s => s.streamId)
  const creatorGoal = useStreamingStore(s => s.currentStreamGoal)
  const creatorViewerCount = useStreamingStore(s => s.viewerCount ?? 0)
  const creatorTipsAmount = useStreamingStore(s => s.tipsAmount ?? 0)
  const openViewersModal = useStreamingStore(s => s.openViewersModal)
  const openTipModalCreator = useStreamingStore(s => s.openTipModal)
  const toggleCommentsActive = useStreamingStore(s => s.toggleCommentsActive)

  const viewerStreamId = useViewerStore(s => s.streamId)
  const viewerGoal = useViewerStore(s => s.currentStreamGoal)
  const viewerCount = useViewerStore(s => s.viewerCount ?? 0)
  const viewerShowTips = useViewerStore(s => s.showTips)
  const viewerTipsAmount = useViewerStore(s => s.tipsAmount ?? 0)
  const toggleCommentsViewer = useViewerStore(s => s.toggleComments)

  const goal = role === 'creator' ? creatorGoal : viewerGoal

  const goalEnabled =
    role === 'creator'
      ? !!creatorStreamId &&
        !!creatorGoal &&
        creatorGoal.amount_goal != null &&
        Number(creatorGoal.amount_goal) > 0
      : !!viewerStreamId &&
        !!viewerGoal &&
        viewerGoal.amount_goal != null &&
        Number(viewerGoal.amount_goal) > 0 &&
        viewerGoal.public !== false &&
        viewerGoal.completed !== true

  const reward = goal?.reward?.trim()
  const showReward = !!reward && goalEnabled

  const openTipModal =
    role === 'creator' ? openTipModalCreator : props.onOpenTipModal

  const toggleComments =
    props.onToggleComments ??
    (role === 'creator' ? toggleCommentsActive : toggleCommentsViewer)

  return (
    <>
      <Container>
        <TopHeader>
          <UserNameContainer>
            <IconCaret />
            <UserName>{creatorUsername}</UserName>
          </UserNameContainer>

          <IconsWrapper>
            {role === 'creator' && (
              <SettingsIcon
                onClick={() => {}}
                src={AllIcons.button_settings}
              />
            )}

            <CloseButton onClick={props.onRequestClose}  opacity={0.5} />
          </IconsWrapper>
        </TopHeader>

        <StatusContainer>
          {role === 'creator' && isStreaming && (
            <LiveStats
              type='viewers'
              clickFn={openViewersModal}
              count={creatorViewerCount}
              showTips={true}
              tipsAmount={creatorTipsAmount}
            />
          )}

          {role === 'viewer' && (
            <LiveStats
              type='viewers'
              count={viewerCount}
              showTips={viewerShowTips}
              tipsAmount={viewerTipsAmount}
            />
          )}

          {goalEnabled && goal && (
            <LiveStats
              type='goal'
              clickFn={openTipModal}
              goalTipped={goal.amount_tipped ?? 0}
              goalTotal={goal.amount_goal ?? 0}
            />
          )}
        </StatusContainer>

        {showReward && (
          <RewardContainer>
            <RewardText>{reward}</RewardText>
          </RewardContainer>
        )}
      </Container>

      {role === 'viewer' && (
        <ViewerLiveActionsContainer>
          <LiveActions
            role='viewer'
            openTipModal={openTipModal}
            toggleComments={toggleComments}
            avatarUrl={avatarUrl}
            commentCount={commentCount}
          />
        </ViewerLiveActionsContainer>
      )}
    </>
  )
}

const Container = styled.header`
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: fixed;
  top: 25px;
  left: 20px;
  right: 20px;
  z-index: 9;
`

const TopHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const UserNameContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 20px;
`

const UserName = styled.p`
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.32px;
  color: #fff;
`

const StatusContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
`

const ViewerLiveActionsContainer = styled.div`
  position: fixed;
  top: 9rem;
  right: 20px;
  z-index: 10;
  pointer-events: auto;
`

const SettingsIcon = styled.img`
  margin-right: 3rem;
  cursor: pointer;
  opacity: 0.5;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`

const IconsWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  opacity: 0.5;
  align-items: center;
`

const RewardContainer = styled.div`
  margin-top: 4px;
`

const RewardText = styled.div`
  font-size: 13px;
  color: #fff;
  opacity: 0.85;
  line-height: 1.4;
`